import React, { useState } from "react";
import { BlockStack, Modal, Text, Spinner, Card } from "@shopify/polaris";
import DOMPurify from "dompurify";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import moment from "moment";

function OptmisedContent({ open, onClose, article, shopOrigin }) {
  const navigate = useNavigate();
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishError, setPublishError] = useState(null);

  if (!article) {
    return (
      <Modal open={open} onClose={onClose} title="Optimized Article Preview">
        <Modal.Section>
          <Text>No content available.</Text>
        </Modal.Section>
      </Modal>
    );
  }

  // Extract the actual article data from the nested structure
  const actualArticle = article[0] || article;

  // Check if actualArticle has the required properties
  if (!actualArticle.title && !actualArticle.content) {
    return (
      <Modal open={open} onClose={onClose} title="Optimized Article Preview">
        <Modal.Section>
          <Text>Article data is malformed.</Text>
        </Modal.Section>
      </Modal>
    );
  }

  // Custom styles for modal content
  const modalContentStyles = `
    .Polaris-Modal-Dialog__Modal {
      max-width: 65% !important;
    }
    .generated-article-modal-content {
      line-height: 1.5;
      font-size: 14px;
      color: #2c2c2c;
    }
    .generated-article-modal-content h1{
      font-size: 28px;
      font-weight: 700;
      margin: 0 0 15px 0;
    }
    .generated-article-modal-content h2 {
      font-weight: 700;
      font-size: 1.45em;
      border-radius: 4px;
      margin-bottom: 12px
    }
    .generated-article-modal-content h3 {
      font-weight: 600;
      font-size: 1.25em;
      margin: 0 0 12px 0;
    }
    .generated-article-modal-content p {
      margin: 0 0 15px 0;
    }
    .generated-article-modal-content ul {
      margin: 0px 0 0px 15px;
      padding-left: 20px;
      list-style-type: disc;
    }
    .generated-article-modal-content li {
      margin-bottom: 8px;
    }
    .generated-article-modal-content strong {
      font-weight: 700;
    }
    .generated-article-modal-content em {
      font-style: italic;
    }
    .generated-article-modal-content br.newline-block {
      display: block;
      margin-bottom: 12px;
    }
    .Polaris-Modal-Header__Title {
      font-size: 17px !important;
    }
    .generated-article-modal-content u,
    .generated-article-modal-content .underline {
      text-decoration: underline;
    }
    .generated-article-modal-content s,
    .generated-article-modal-content strike,
    .generated-article-modal-content .strikethrough {
      text-decoration: line-through;
    }
    .generated-article-modal-content mark {
      background-color: #ffe066;
    }
  `;

  // Sanitize the content
  const sanitizedContent = DOMPurify.sanitize(actualArticle.content, {
    ALLOWED_TAGS: [
      "p", "br", "strong", "em", "ul", "li", "a", "h1", "h2", "h3",
      "span", "img", "div", "table", "u", "s", "strike", "mark"
    ],
    ALLOWED_ATTR: [
      "href", "src", "alt", "width", "height", "style", "data-contrast", "class"
    ]
  });

  const formattedContent = sanitizedContent.replace(/\n\n\n\n/g, '<br class="newline-block">');

  const handleSaveAsDraft = async () => {
    console.log("Save Article clicked");
    setIsPublishing(true);
    setPublishError(null);

    try {
      // Extract current article_id from URL
      const urlParams = new URLSearchParams(window.location.search);
      const articleIdParam = window.location.pathname.split('/').pop();
      const articleId = articleIdParam.includes('=') 
        ? articleIdParam.split('=')[1] 
        : articleIdParam;

      // Prepare data payload matching your handlePublish structure
      const data = {
        article_id: articleId,
        article_title: actualArticle.title,
        article_content: actualArticle.content,
        status: "draft", // Save as draft by default
        blog_id: null, // Can be set if you have blog selection logic
        tags: [], // Can be populated if needed
        scheduled_datetime: null,
        auto_publish_toggle: false,
      };

      console.log("Publishing data:", data);

      const response = await axios.post(
        `/publish-article/${shopOrigin}/`,
        data,
        {
          headers: {
            "X-CSRFToken": document.querySelector("[name=csrfmiddlewaretoken]")?.value,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      if (response.data.status === "success") {
        const newArticleId = response.data.article_id?.replace(
          "gid://shopify/Article/",
          ""
        );

        console.log("✅ Article saved successfully:", newArticleId);

        // Show success toast if available
        if (window.shopify?.toast) {
          window.shopify.toast.show("Article saved as draft successfully!");
        }

        // Close modal
        onClose();
        window.location.reload();
      }
    } catch (error) {
      console.error("❌ Publish error:", error);
      setPublishError(
        error.response?.data?.message || "Failed to save article. Please try again."
      );
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <>
      <style>{modalContentStyles}</style>
      <Modal
        open={open}
        onClose={onClose}
        title="Generated Article Preview"
        primaryAction={{
          content: isPublishing ? "Saving..." : "Save Article",
          onAction: handleSaveAsDraft,
          loading: isPublishing,
          disabled: isPublishing,
        }}
        secondaryActions={[
          {
            content: "Cancel",
            onAction: onClose,
            disabled: isPublishing,
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {/* Show error message if save fails */}
            {publishError && (
              <div style={{ 
                padding: "12px", 
                backgroundColor: "#FEF1F2", 
                borderRadius: "8px",
                border: "1px solid #FED3D1"
              }}>
                <Text variant="bodySm" tone="critical">
                  {publishError}
                </Text>
              </div>
            )}

            {/* Title */}
            <Text variant="headingLg" as="h2">
              {actualArticle.title || 'Untitled'}
            </Text>

            {/* Description */}
            {actualArticle.descriptive && (
              <Text variant="bodyMd" tone="subdued">
                {actualArticle.descriptive}
              </Text>
            )}

            {/* Read Time */}
            {actualArticle.readtime && (
              <Text variant="bodySm">
                {actualArticle.readtime}
              </Text>
            )}

            {/* Content with HTML */}
            <div
              className="generated-article-modal-content"
              style={{
                border: '1px solid #e1e3e5',
                borderRadius: '8px',
                padding: '16px',
                maxHeight: '500px',
                overflowY: 'auto',
              }}
              dangerouslySetInnerHTML={{ __html: formattedContent }}
            />
            {/* BlogHero Promo Message - Polaris Card */}
            <div style={{ marginTop: "12px" }}>
              <Card background="bg-surface-brand-selected">
                <BlockStack gap="200">
                  <Text variant="bodyMd">
                    ✨ For better AI editing and advanced rich-text tools, try{" "}
                    <a
                      href="https://apps.shopify.com/bloghero"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontWeight: 600, color: "#006fbb" }}
                    >
                      BlogHero
                    </a>.
                  </Text>
                </BlockStack>
              </Card>
            </div>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </>
  );
}

export default OptmisedContent;